import { Suspense, lazy, startTransition, useContext, useEffect, useState } from 'react';

import CookieConsent from 'react-cookie-consent';
import { appDataContext } from '@/context';
import styled from 'styled-components';

const ModalCookieDetails = lazy(() => import('@/components/Modals/ModalCookieDetails'));

const RccCookie = () => {
  const dataContext = useContext(appDataContext);
  const text = dataContext?.lang;

  const [, setIsNecessaryEnabled] = useState(true);
  const [analyticsEnabled, setIsAnalyticsEnabled] = useState(false);
  const [, setIsMarketingEnabled] = useState(false);
  const [cookieVisible, setCookieVisible] = useState('byCookieValue');
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    // requestAnimationFrame fires after the next browser paint (FCP + ~16ms).
    // startTransition alone defers ~1s on 4x throttled CPU (React treats it as low-priority
    // and waits for all urgent work to finish). rAF fires right after the browser commits
    // FCP, when the CPU has nothing urgent — so startTransition executes immediately.
    // Cookie popup appears at FCP + ~32ms instead of FCP + 1s → LCP ≈ FCP.
    const id = requestAnimationFrame(() => {
      startTransition(() => {
        try {
          const stored = localStorage.getItem('rcc_consent');
          if (stored) {
            const parsed = JSON.parse(stored);
            setIsNecessaryEnabled(parsed?.necessary || true);
            setIsAnalyticsEnabled(parsed?.analytics || false);
            setIsMarketingEnabled(parsed?.marketing || false);
            return;
          }
        } catch {
          // ignore
        }
        setIsClient(true);
      });
    });
    return () => cancelAnimationFrame(id);
  }, []);

  const saveConsent = (value) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('rcc_consent', JSON.stringify(value));
      document.cookie = `rcc_consent=${JSON.stringify(value)}; path=/; max-age=31536000`;
    }
  };

  const acceptCookieConsentAll = () => {
    !analyticsEnabled && setIsAnalyticsEnabled(true);
    setIsMarketingEnabled(true);
    setIsNecessaryEnabled(true);
    saveConsent({ necessary: true, analytics: true, marketing: true });
    closeCookieConsent();
  };

  const declineCookieConsentAll = () => {
    analyticsEnabled && setIsAnalyticsEnabled(false);
    setIsMarketingEnabled(false);
    setIsNecessaryEnabled(true);
    saveConsent({ necessary: true, analytics: false, marketing: false });
    closeCookieConsent();
  };

  const closeCookieConsent = () => {
    setCookieVisible('hidden');
    document?.body.classList.remove('modal-open');
  };

  useEffect(() => {
    const handleDisplayCookie = () => {
      setIsClient(true);
      setCookieVisible('show');
    };
    window.addEventListener('displayCookie', handleDisplayCookie);
    return () => window.removeEventListener('displayCookie', handleDisplayCookie);
  }, []);

  // Render SC wrappers even before client-side check, so their CSS is included in
  // SSR's <style data-styled>. When the popup transitions to visible, SC finds the
  // styles already in its registry → adopts them → 0ms paint delay (no new injection).
  // Both SSR and client first render see isClient=false → identical output → no mismatch.
  if (!isClient) {
    return (
      <RccCookieontainer style={{ display: 'none' }} aria-hidden='true'>
        <CookieTextWrapper />
      </RccCookieontainer>
    );
  }

  return (
    <RccCookieontainer className='delayDisplay'>
      <CookieConsent
          visible={cookieVisible}
          location='bottom'
          buttonText={text?.cookieConfig?.accept}
          declineButtonText={text?.cookieConfig?.refuse}
          cookieName='rcc_consent'
          containerClasses='cookie_container'
          buttonWrapperClasses='cookie_button_wrapper'
          buttonClasses='cookie_button'
          declineButtonClasses='cookie_button'
          overlayStyle={{ background: 'rgba(0, 0, 0, 0.15)' }}
          style={{ background: 'white', color: 'black', height: 'auto' }}
          expires={0.05}
          onAccept={acceptCookieConsentAll}
          enableDeclineButton
          hideOnDecline
          onDecline={declineCookieConsentAll}
          buttonStyle={{
            padding: '10px 20px',
            border: '1px solid #006C95',
            backgroundColor: '#006C95',
            color: '#fff',
            marginLeft: '0',
            marginRight: '0',
            borderRadius: '4px',
            marginBottom: '0',
            marginTop: '0',
            textTransform: 'uppercase',
            display: 'inline-lock',
            fontWeight: 'bold',
            fontSize: '14px',
          }}
          declineButtonStyle={{
            padding: '10px 20px',
            border: '1px solid #006C95',
            color: '#fff',
            backgroundColor: '#006C95',
            marginLeft: '0',
            marginRight: '0',
            borderRadius: '4px',
            marginBottom: '0',
            marginTop: '0',
            textTransform: 'uppercase',
            display: 'inline-lock',
            fontWeight: 'bold',
            fontSize: '14px',
          }}>
          <CookieTextWrapper>
            <h4>{text?.cookieConfig?.popup1?.title}</h4>
            <div className='cookie_text'>
              {text?.cookieConfig?.popup1?.content}
              <Suspense>
                <ModalCookieDetails
                  modalButton={text?.cookieConfig?.popup1?.info}
                  portalClassName='modal_portal'
                  className='modal_content right'
                  overlayClassName='modal_overlay'
                  triggerClose={closeCookieConsent}
                  text={text}
                  bodyOpenClassName='modal-open'
                />
              </Suspense>
              {'.'}
            </div>
          </CookieTextWrapper>
      </CookieConsent>
    </RccCookieontainer>
  );
};

export default RccCookie;

const RccCookieontainer = styled.div.attrs({
  className: 'RccCookie',
})`
  z-index: 200;
  .cookie_container {
    position: relative;
    padding: 0px 5px 5px !important;
    @media screen and (min-width: 800px) {
      padding: 10px 30px 30px !important;
    }
    & div {
      @media screen and (max-width: 800px) {
        margin: 2px !important;
      }
    }
  }
  .modal_portal {
    position: fixed;
    inset: 0;
    z-index: 999;
    background-color: rgba(0, 0, 0, 0.15);
  }
  .modal_content {
    position: fixed;
    z-index: 999;
    justify-content: center;
    align-items: center;
  }
  .modal_content.noWhiteBox { background-color: transparent; }
  .modal_content.offerCardPopup.noWhiteBox {
    @media screen and (min-width: 800px) {
      max-width: 60vw;
      max-height: 70vh;
      overflow-y: hidden;
      top: 0; left: 0; right: 0; padding: 0;
    }
    background-color: transparent;
    margin: auto;
    position: fixed;
    z-index: 10;
    border-radius: 4px;
    box-shadow: none;
    inset: 0px;
    .modal_close svg { color: white; fill: white; }
    > div { max-height: 100%; display: flex; }
  }
  .modal_content.center {
    max-width: 60vw; max-height: 60vh;
    top: 0; left: 0; right: 0; bottom: 0; padding: 0; margin: auto;
  }
  .modal_content.right {
    top: 0; left: 0; right: 0; bottom: 0; padding: 0;
    @media screen and (min-width: 860px) {
      max-width: 60%; top: 0; left: 60%; right: 0; bottom: 0; padding: 0;
    }
    overflow: auto;
  }
  .cookie_button_wrapper {
    display: flex; justify-content: center; align-items: center;
    gap: 10px; margin-top: 10px;
  }
  .cookie_button {
    @media screen and (min-width: 800px) { margin-top: 15px !important; }
  }
  .modal-details-popup { max-height: 100%; overflow-y: auto; }
  .accountCreationPopup {
    flex: 1 1 auto; max-height: 100%; overflow-y: auto;
    max-width: 60vw; margin: 0 auto;
  }
  @keyframes fadein {
    0% { opacity: 0; }
    66% { opacity: 0; }
    100% { opacity: 1; }
  }
  @-webkit-keyframes fadein {
    0% { opacity: 0; }
    66% { opacity: 0; }
    100% { opacity: 1; }
  }
  -webkit-animation: 0.3s ease 0s normal forwards 1 fadein;
  animation: 0.3s ease 0s normal forwards 1 fadein;
`;

const CookieTextWrapper = styled.div.attrs({
  className: 'CookieTextWrapper',
})`
  display: flex;
  flex-direction: column;
  h4 {
    font-family: ${(props) => props.theme.fonts.body};
    font-size: 1.6rem;
    @media screen and (max-width: 800px) { display: none; }
  }
  .cookie_text {
    font-family: ${(props) => props.theme.fonts.body};
    font-size: 1.4rem;
    & div {
      display: inline-block !important;
      & button::first-letter { text-transform: none; }
    }
  }
  gap: 6px;
  @media screen and (min-width: 800px) { gap: 16px; margin-bottom: 16px; }
`;
